import React, { useEffect, useRef } from "react";
import styled from "styled-components";

const FocusContainerDiv = styled.div`
  display: inherit;
  flex-direction: inherit;
  flex: inherit;
`;

const focusableElements: string = ["button", "select", "textarea", "input", "[href]", "[tabindex]"]
  .map((element) => `${element}:not([disabled]):not([type="hidden"]):not([tabindex="-1"])`)
  .join(",");

const getFocusableElements = (refCurr: HTMLDivElement): NodeList => {
  return refCurr?.querySelectorAll(focusableElements);
};

type FocusContainerProps = {
  focusFirstElementOnMount?: boolean;
};

export const FocusContainer: React.FC<FocusContainerProps> = ({ focusFirstElementOnMount = true, children }) => {
  const ref = useRef<HTMLDivElement>(null);

  const keyDownHandler = (e: KeyboardEvent): void => {
    const isTabPressed = e.key === "Tab";

    if (!isTabPressed || !ref.current) {
      return;
    }

    const focusableContent = getFocusableElements(ref.current);
    const firstFocusableElement = focusableContent[0] as HTMLElement;
    const lastFocusableElement = focusableContent[focusableContent.length - 1] as HTMLElement;

    if (e.shiftKey) {
      if (document.activeElement === firstFocusableElement) {
        lastFocusableElement.focus();
        e.preventDefault();
      }
    } else if (document.activeElement === lastFocusableElement) {
      firstFocusableElement.focus();
      e.preventDefault();
    }
  };

  // Add keydown handler on mount
  useEffect(() => {
    const el = ref.current;

    if (!el) {
      return;
    }

    el.addEventListener("keydown", keyDownHandler);
    // Remove keydown handler on unmount
    return () => {
      el.removeEventListener("keydown", keyDownHandler);
    };
  }, []);

  // Focus first element
  useEffect(() => {
    if (ref?.current && focusFirstElementOnMount) {
      const focusableContent = getFocusableElements(ref.current);
      const firstFocusableElement: HTMLElement = focusableContent[0] as HTMLElement;

      if (firstFocusableElement) {
        firstFocusableElement.focus();
      }
    }
  }, [ref, focusFirstElementOnMount]);

  return (
    <FocusContainerDiv data-testid="focus-container" ref={ref}>
      {children}
    </FocusContainerDiv>
  );
};
